'use client';

import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from 'react';
import type { VtkDatasetInfo, VtkPolylineSet } from '../types/vtk';
import { loadVtkModules } from '../lib/loadVtkModules';
import {
  createVtkScene,
  type CameraState,
  type VtkScene,
} from '../lib/createVtkScene';
import {
  applyPaletteToMapper,
  replaceScalarBar,
  resolveScalarRange,
  resolveRobustScalarRange,
  type ScalarRange,
} from '../lib/vtkScalarStyling';

export type VtkViewerHandle = {
  resetCamera: () => void;
  setCameraState: (state: CameraState) => void;
};

type Props = {
  dataset: VtkDatasetInfo | null;
  activeScalar?: string | null;
  activeScalarLabel?: string | null;
  streamlines?: VtkPolylineSet | null;
  streamlineScalar?: string | null;
  streamlineScalarLabel?: string | null;
  showStreamlines?: boolean;
  meshVisible?: boolean;
  status?: 'ready' | 'computing';
  computingStreamlines?: boolean;
  forcedScalarRange?: ScalarRange | null;
};

function captureCamera(renderer: VtkScene['renderer']): CameraState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cam = (renderer as any).getActiveCamera();
  return {
    pos: [...cam.getPosition()],
    fp: [...cam.getFocalPoint()],
    up: [...cam.getViewUp()],
    cr: [...cam.getClippingRange()],
  };
}

function restoreCamera(renderer: VtkScene['renderer'], state: CameraState) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cam = (renderer as any).getActiveCamera();
  cam.setPosition(...state.pos);
  cam.setFocalPoint(...state.fp);
  cam.setViewUp(...state.up);
  cam.setClippingRange(...state.cr);
}

function getActiveScalarEntry(
  dataset: VtkDatasetInfo,
  activeScalar: string | null,
) {
  if (activeScalar && dataset.pointData[activeScalar]) {
    return dataset.pointData[activeScalar];
  }

  return Object.values(dataset.pointData).find(
    (arr) => arr.numberOfComponents === 1,
  );
}

async function applyDatasetToScene(
  scene: VtkScene,
  dataset: VtkDatasetInfo,
  activeScalar: string | null,
  activeScalarLabel: string | null,
  initialCameraState: React.RefObject<CameraState | null>,
  meshVisible: boolean,
  forcedScalarRange: ScalarRange | null,
) {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const vtk = await loadVtkModules();

  const hadPreviousData =
    ((scene.polydata as any).getPoints?.()?.getNumberOfPoints?.() ?? 0) > 0;

  const previousCamera = hadPreviousData ? captureCamera(scene.renderer) : null;

  const points = (vtk.vtkPoints as any).newInstance();
  points.setData(new Float32Array(dataset.points.flat()), 3);
  (scene.polydata as any).setPoints(points);

  if (dataset.cells.connectivity.length > 0) {
    const flatCells: number[] = [];
    for (const cell of dataset.cells.connectivity) {
      flatCells.push(cell.length, ...cell);
    }

    const cellArray = (vtk.vtkCellArray as any).newInstance({
      values: Uint32Array.from(flatCells),
    });

    (scene.polydata as any).setPolys(cellArray);
  } else {
    (scene.polydata as any).setPolys(
      (vtk.vtkCellArray as any).newInstance({ values: new Uint32Array() }),
    );
  }

  const scalarEntry = getActiveScalarEntry(dataset, activeScalar);

  if (scene.scalarBarActor) {
    (scene.renderer as any).removeActor2D(scene.scalarBarActor);
    (scene.scalarBarActor as any).delete?.();
    scene.scalarBarActor = null;
  }

  if (
    scalarEntry &&
    scalarEntry.numberOfComponents === 1 &&
    Array.isArray(scalarEntry.values) &&
    scalarEntry.values.length > 0
  ) {
    const scalarValues = scalarEntry.values as number[];

    (scene.polydata as any).getPointData().setScalars(
      (vtk.vtkDataArray as any).newInstance({
        name: scalarEntry.name,
        values: Float32Array.from(scalarValues),
        numberOfComponents: 1,
      }),
    );

    (scene.mapper as any).setScalarVisibility(true);
    (scene.mapper as any).setColorModeToMapScalars();
    (scene.mapper as any).setScalarModeToUsePointData();

    const resolvedRange = resolveScalarRange(scalarValues, forcedScalarRange);

    if (resolvedRange) {
      const lut = applyPaletteToMapper(vtk, scene.mapper, resolvedRange);
      replaceScalarBar(
        vtk,
        scene,
        lut,
        activeScalarLabel || scalarEntry.name || activeScalar || 'Scalar',
      );
    } else {
      (scene.mapper as any).setScalarVisibility(false);
    }
  } else {
    (scene.polydata as any).getPointData().setScalars(null);
    (scene.mapper as any).setScalarVisibility(false);
  }

  (scene.actor as any).setVisibility(meshVisible);

  (scene.polydata as any).modified();
  (scene.mapper as any).modified();

  if (previousCamera) {
    restoreCamera(scene.renderer, previousCamera);
  } else if (meshVisible) {
    (scene.renderer as any).resetCamera();
    (scene.renderer as any).resetCameraClippingRange();
    // Extra zoom (default zoom ~2 fits model bigger)
    (scene.renderer as any).getActiveCamera().zoom(6);
    initialCameraState.current = captureCamera(scene.renderer);
  }

  (scene.renderWindow as any).render();
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

async function applyStreamlinesToScene(
  scene: VtkScene,
  streamlines: VtkPolylineSet | null,
  streamlineScalar: string | null,
  streamlineScalarLabel: string | null,
  showStreamlines: boolean,
) {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const vtk = await loadVtkModules();

  (scene.streamlineActor as any).setVisibility(
    showStreamlines && !!streamlines,
  );

  if (!showStreamlines || !streamlines) {
    (scene.streamlinePolydata as any).setPoints(
      (vtk.vtkPoints as any).newInstance(),
    );
    (scene.streamlinePolydata as any).setLines(
      (vtk.vtkCellArray as any).newInstance({ values: new Uint32Array() }),
    );
    (scene.streamlinePolydata as any).getPointData().setScalars(null);
    (scene.streamlineMapper as any).setScalarVisibility(false);

    (scene.renderWindow as any).render();
    return;
  }

  const points = (vtk.vtkPoints as any).newInstance();
  points.setData(new Float32Array(streamlines.points.flat()), 3);
  (scene.streamlinePolydata as any).setPoints(points);

  const flatLines: number[] = [];
  for (const line of streamlines.lines) {
    flatLines.push(line.length, ...line);
  }

  (scene.streamlinePolydata as any).setLines(
    (vtk.vtkCellArray as any).newInstance({
      values: Uint32Array.from(flatLines),
    }),
  );

  const scalarEntry =
    streamlineScalar && streamlines.pointData[streamlineScalar]
      ? streamlines.pointData[streamlineScalar]
      : null;

  if (
    scalarEntry &&
    scalarEntry.numberOfComponents === 1 &&
    Array.isArray(scalarEntry.values) &&
    scalarEntry.values.length > 0
  ) {
    const scalarValues = scalarEntry.values as number[];

    (scene.streamlinePolydata as any).getPointData().setScalars(
      (vtk.vtkDataArray as any).newInstance({
        name: scalarEntry.name,
        values: Float32Array.from(scalarValues),
        numberOfComponents: 1,
      }),
    );

    (scene.streamlineMapper as any).setScalarVisibility(true);
    (scene.streamlineMapper as any).setColorModeToMapScalars();
    (scene.streamlineMapper as any).setScalarModeToUsePointData();

    const resolvedRange = resolveScalarRange(scalarValues, null);
    
    //const resolvedRange = resolveRobustScalarRange(scalarValues, null, 1, 99);
    if (resolvedRange) {
      const lut = applyPaletteToMapper(
        vtk,
        scene.streamlineMapper,
        resolvedRange,
      );
      replaceScalarBar(
        vtk,
        scene,
        lut,
        streamlineScalarLabel ||
          scalarEntry.name ||
          streamlineScalar ||
          'Scalar',
      );
    }
  } else {
    (scene.streamlinePolydata as any).getPointData().setScalars(null);
    (scene.streamlineMapper as any).setScalarVisibility(false);
    (scene.streamlineActor as any).getProperty().setColor(1, 1, 1);

    if (scene.scalarBarActor) {
      (scene.renderer as any).removeActor2D(scene.scalarBarActor);
      (scene.scalarBarActor as any).delete?.();
      scene.scalarBarActor = null;
    }
  }

  (scene.streamlinePolydata as any).modified();
  (scene.streamlineMapper as any).modified();

  (scene.renderWindow as any).render();
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

export default forwardRef<VtkViewerHandle, Props>(function VtkViewer(
  {
    dataset,
    activeScalar = null,
    activeScalarLabel = null,
    streamlines = null,
    streamlineScalar = null,
    streamlineScalarLabel = null,
    showStreamlines = true,
    meshVisible = true,
    status = 'ready',
    computingStreamlines = false,
    forcedScalarRange = null,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<VtkScene | null>(null);
  const initialCameraState = useRef<CameraState | null>(null);
  const pipelineReady = useRef<Promise<void>>(Promise.resolve());

  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    value: string;
    label: string;
  } | null>(null);
  const scalarLabelRef = useRef(activeScalarLabel);
  const streamlineScalarLabelRef = useRef(streamlineScalarLabel);

  scalarLabelRef.current = activeScalarLabel;
  streamlineScalarLabelRef.current = streamlineScalarLabel;

  useEffect(() => {
    let mounted = true;
    let resolveReady!: () => void;
    pipelineReady.current = new Promise<void>((resolve) => {
      resolveReady = resolve;
    });

    let resizeObserver: ResizeObserver | null = null;

    async function init() {
      const container = containerRef.current;
      if (!container) {
        resolveReady();
        return;
      }

      await loadVtkModules();
      if (!mounted) {
        resolveReady();
        return;
      }

      const vtk = await loadVtkModules();
      const scene = createVtkScene(vtk, container);
      sceneRef.current = scene;

      // Click-to-pick: show tooltip with scalar value at clicked point
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const pointPicker = (vtk.vtkPointPicker as any).newInstance();
      pointPicker.setTolerance(0.005);

      // Rotate around picked point: on first drag move after left press, set focal point to mesh hit
      let rotating = false;
      let pressedPos = { x: 0, y: 0 };

      const onDragStart = (e: PointerEvent) => {
        if (e.button !== 0) return;
        rotating = false;
        const rect = container.getBoundingClientRect();
        pressedPos = {
          x: e.clientX - rect.left,
          y: rect.height - (e.clientY - rect.top),
        };
      };
      const onDragMove = (e: PointerEvent) => {
        if (e.buttons !== 1) {
          rotating = false;
          return;
        }
        if (rotating) return; // already set center for this drag
        rotating = true;
        pointPicker.pick([pressedPos.x, pressedPos.y, 0], scene.renderer);
        const ptId = pointPicker.getPointId();
        if (ptId < 0) return;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const pos = (pointPicker as any).getPickedPositions();
        if (!pos || pos.length === 0) return;
        const fp = pos[0];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const cam = (scene.renderer as any).getActiveCamera();
        const oldFp = cam.getFocalPoint();
        cam.setFocalPoint(fp[0], fp[1], fp[2]);
        const p = cam.getPosition();
        cam.setPosition(
          p[0] + fp[0] - oldFp[0],
          p[1] + fp[1] - oldFp[1],
          p[2] + fp[2] - oldFp[2],
        );
      };
      container.addEventListener('pointerdown', onDragStart, { capture: true });
      container.addEventListener('pointermove', onDragMove, { capture: true });

      container.addEventListener('contextmenu', (e: Event) =>
        e.preventDefault(),
      );

      const onRightClickPick = (e: PointerEvent) => {
        if (e.button !== 2) return; // right click only

        const rect = container.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = rect.height - (e.clientY - rect.top);

        pointPicker.pick([x, y, 0], scene.renderer);
        const ptId = pointPicker.getPointId();
        if (ptId < 0) {
          setTooltip(null);
          return;
        }

        // Determine which prop was picked — streamline vs mesh
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const pickedActors: any[] = (pointPicker as any).getActors();
        const pickedStreamline =
          pickedActors.indexOf(scene.streamlineActor) !== -1;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const pd: any = pickedStreamline
          ? scene.streamlinePolydata
          : scene.polydata;
        const scalars = pd?.getPointData?.()?.getScalars?.();
        if (!scalars) return;

        const numComps = scalars.getNumberOfComponents();
        const tuple = scalars.getTuple(ptId);
        const val = numComps === 1 ? tuple : tuple?.[0];

        const defaultLabel = pickedStreamline
          ? streamlineScalarLabelRef.current || 'Velocity Magnitude (m/s)'
          : scalarLabelRef.current || 'Scalar';
        const label = scalars.getName?.() || defaultLabel;

        setTooltip({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
          value: val != null ? Number(val).toFixed(4) : '—',
          label,
        });
        // Hide tooltip on mouse move
        container.addEventListener('pointermove', () => setTooltip(null), {
          once: true,
        });
      };
      container.addEventListener('pointerup', onRightClickPick);

      resizeObserver = new ResizeObserver(() => {
        scene.resize();
      });
      resizeObserver.observe(container);

      resolveReady();
    }

    init().catch((err) => {
      console.error('VtkViewer init error:', err);
      resolveReady();
    });

    return () => {
      mounted = false;
      resizeObserver?.disconnect();
      sceneRef.current?.destroy();
      sceneRef.current = null;
      initialCameraState.current = null;
      resolveReady();
    };
  }, []);

  useEffect(() => {
    async function update() {
      await pipelineReady.current;
      const scene = sceneRef.current;
      if (!scene) return;

      if (!dataset || !meshVisible) {
        (scene.actor as any).setVisibility(false); // eslint-disable-line @typescript-eslint/no-explicit-any
        (scene.renderWindow as any).render(); // eslint-disable-line @typescript-eslint/no-explicit-any
        return;
      }

      await applyDatasetToScene(
        scene,
        dataset,
        activeScalar,
        activeScalarLabel,
        initialCameraState,
        meshVisible,
        forcedScalarRange,
      );
    }

    void update();
  }, [
    dataset,
    activeScalar,
    activeScalarLabel,
    meshVisible,
    forcedScalarRange,
  ]);

  useEffect(() => {
    async function update() {
      await pipelineReady.current;
      if (!sceneRef.current) return;

      await applyStreamlinesToScene(
        sceneRef.current,
        streamlines,
        streamlineScalar,
        streamlineScalarLabel,
        showStreamlines,
      );
    }

    void update();
  }, [streamlines, streamlineScalar, streamlineScalarLabel, showStreamlines]);

  useImperativeHandle(ref, () => ({
    resetCamera() {
      /* eslint-disable @typescript-eslint/no-explicit-any */
      const scene = sceneRef.current;
      if (!scene) return;

      if (initialCameraState.current) {
        restoreCamera(scene.renderer, initialCameraState.current);
      } else {
        (scene.renderer as any).resetCamera();
        (scene.renderer as any).resetCameraClippingRange();
      }

      (scene.renderWindow as any).render();
      /* eslint-enable @typescript-eslint/no-explicit-any */
    },
    setCameraState(state: CameraState) {
      const scene = sceneRef.current;
      if (!scene) return;
      restoreCamera(scene.renderer, state);
      (scene.renderWindow as any).render(); // eslint-disable-line @typescript-eslint/no-explicit-any
    },
  }));

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 0,
        background: '#111',
        borderRadius: 0,
        overflow: 'hidden',
      }}
    >
      <style>{`
        @keyframes vtk-spin {
          to { transform: rotate(360deg); }
        }
        .vtk-spinner {
          display: inline-block;
          width: 64px;
          height: 64px;
          border: 4px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: vtk-spin 0.7s linear infinite;
        }
      `}</style>

      {status === 'computing' && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
          }}
        >
          <span className='vtk-spinner' />
        </div>
      )}
      {tooltip && (
        <div
          style={{
            position: 'absolute',
            left: tooltip.x + 14,
            top: tooltip.y - 10,
            zIndex: 10,
            padding: '4px 10px',
            borderRadius: 6,
            background: 'rgba(0,0,0,0.75)',
            color: '#fff',
            fontSize: 13,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            fontFamily: 'monospace',
          }}
        >
          {/* {tooltip.label}: */}
          {tooltip.value}
        </div>
      )}
    </div>
  );
});
